import { Box } from "@dropbox/dig-foundations";
import { useDashTrialInfo } from "@mirage/service-dash-trial/hooks/useDashTrialInfo";
import { useNotificationsLastSeen } from "@mirage/service-dash-trial/hooks/useNotificationsLastSeen";
import { useCallback, useEffect, useRef } from "react";
import {
  TRIGGER_CHAT_UPSELL_AFTER_DAYS_LEFT,
  useChatTrialUpsell,
} from "./useChatTrialUpsell";

type ChatTrialUpsellContainerProps = {
  maxWidth?: string;
  numChatTurns: number;
};

const NUM_CHAT_TURNS_TO_SHOW_UPSELL = 3;

export const ChatTrialUpsellContainer = ({
  maxWidth = "760px",
  numChatTurns,
}: ChatTrialUpsellContainerProps) => {
  const {
    showChatTrialNotification,
    setChatContainerRef,
    isChatTrialNotificationVisible,
    hideTrialNotification,
  } = useChatTrialUpsell();
  const containerRef = useRef<HTMLDivElement>(null);
  const { daysRemainingInDashTrial } = useDashTrialInfo();
  const { lastSeenChatNotification } = useNotificationsLastSeen();

  useEffect(() => {
    hideTrialNotification(); // when entering chat hide visible notifications from other components
    setChatContainerRef(containerRef);

    return () => {
      // reset container and hide notification so it doesn't appear in wrong place in the DOM
      setChatContainerRef(null);
      hideTrialNotification();
    };
  }, [setChatContainerRef]);

  const checkIfShouldShowChatTrialUpsell = useCallback(
    (numChatTurns: number) => {
      if (
        numChatTurns === NUM_CHAT_TURNS_TO_SHOW_UPSELL &&
        daysRemainingInDashTrial <= TRIGGER_CHAT_UPSELL_AFTER_DAYS_LEFT &&
        daysRemainingInDashTrial < lastSeenChatNotification // don't show notification if it's been shown today.
      ) {
        showChatTrialNotification(daysRemainingInDashTrial);
      }
    },
    [
      daysRemainingInDashTrial,
      lastSeenChatNotification,
      showChatTrialNotification,
    ],
  );

  useEffect(() => {
    checkIfShouldShowChatTrialUpsell(numChatTurns);
  }, [numChatTurns]);

  const paddingAmount = isChatTrialNotificationVisible ? "12" : "0";

  return (
    <Box
      ref={containerRef}
      style={{ maxWidth }}
      width="100%"
      paddingLeft={paddingAmount}
      paddingRight={paddingAmount}
      paddingBottom={paddingAmount}
      backgroundColor="transparent"
      data-testid="chat-trial-upsell-container"
    />
  );
};
